import { memo, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNotify, useTranslate, useLocale } from 'react-admin';

import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { DashboardContent } from 'src/layouts/dashboard';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { Iconify } from 'src/components/iconify';
import { PermissionButton } from 'src/routes/components';

import { useWorldCupMatchData } from './hooks/useWorldCupMatchData';
import type {
  MatchDataKpi,
  MatchesByStage,
  MatchStatus,
  MatchSyncStatus,
  PatchWorldCupMatchPayload,
  WorldCupMatch,
} from './types';
import {
  STAGE_RESPONSE_ORDER,
  formatInt,
  formatRelativeTime,
  formatShortTime,
  getMatchScore,
  getMatchStatusKey,
  getMatchTimeText,
  getTeamName,
} from './utils';

const I18N_PREFIX = 'module.worldCup.matchData';

type TranslateFn = ReturnType<typeof useTranslate>;

type ChipColor = 'default' | 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';

type KpiBorderColor = 'primary.main' | 'success.main' | 'info.main' | 'warning.main' | 'divider';

type KpiCardItem = {
  title: string;
  value: string;
  borderColor: KpiBorderColor;
};

type StageSection = {
  key: string;
  title: string;
  count: number;
  rows: WorldCupMatch[];
};

const tMatchData = (t: TranslateFn, key: string, options?: Record<string, unknown>) =>
  t(`${I18N_PREFIX}.${key}`, options);

function getMatchStatusColor(status?: MatchStatus): ChipColor {
  switch (status) {
    case 'live':
      return 'success';
    case 'scheduled':
      return 'info';
    case 'finished':
      return 'default';
    case 'cancelled':
      return 'error';
    case 'postponed':
      return 'warning';
    default:
      return 'default';
  }
}

export const MainPage = () => {
  const t = useTranslate();
  const notify = useNotify();

  const { kpi, matchesByStage, syncStatus, loading, patchMatch } = useWorldCupMatchData();

  const handlePatchMatch = useCallback(
    async (matchId: string, payload: PatchWorldCupMatchPayload) => {
      try {
        await patchMatch(matchId, payload);
        notify(tMatchData(t, 'notify.updateSuccess'), { type: 'success' });
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        notify(message || tMatchData(t, 'notify.updateFailed'), { type: 'error' });
        throw err;
      }
    },
    [notify, patchMatch, t]
  );

  const kpiCards = useMemo(() => buildKpiCards(kpi, t), [kpi, t]);

  const stageSections = useMemo(() => buildStageSections(matchesByStage, t), [matchesByStage, t]);

  return (
    <DashboardContent>
      <CustomBreadcrumbs
        heading={tMatchData(t, 'title')}
        links={[{ name: t('resource.worldCup') }, { name: t('resource.worldCupMatchData') }]}
        sx={{ mb: 3 }}
      />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {tMatchData(t, 'description', { count: kpi?.totalMatches })}
      </Typography>

      {loading && !kpi ? (
        <CenterBox>
          <CircularProgress size={28} />
        </CenterBox>
      ) : (
        <Stack spacing={3}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(2, minmax(0, 1fr))',
                xl: 'repeat(4, minmax(0, 1fr))',
              },
              gap: 2,
            }}
          >
            {kpiCards.map((item) => (
              <KpiCard key={item.title} {...item} />
            ))}
          </Box>

          <SyncStatusCard syncStatus={syncStatus} />

          <Stack spacing={3}>
            {stageSections.map((section) => (
              <MatchStageCard
                key={section.key}
                title={section.title}
                count={section.count}
                rows={section.rows}
                onPatchMatch={handlePatchMatch}
              />
            ))}
          </Stack>
        </Stack>
      )}
    </DashboardContent>
  );
};

function buildKpiCards(kpi: MatchDataKpi | null, t: TranslateFn): KpiCardItem[] {
  return [
    {
      title: tMatchData(t, 'kpi.totalMatches'),
      value: kpi ? formatInt(kpi.totalMatches) : '--',
      borderColor: 'divider',
    },
    {
      title: tMatchData(t, 'kpi.liveCount'),
      value: kpi ? formatInt(kpi.liveCount) : '--',
      borderColor: 'success.main',
    },
    {
      title: tMatchData(t, 'kpi.upcomingCount'),
      value: kpi ? formatInt(kpi.upcomingCount) : '--',
      borderColor: 'primary.main',
    },
    {
      title: tMatchData(t, 'kpi.finishedCount'),
      value: kpi ? formatInt(kpi.finishedCount) : '--',
      borderColor: 'divider',
    },
  ];
}

function buildStageSections(matchesByStage: MatchesByStage | null, t: TranslateFn): StageSection[] {
  return STAGE_RESPONSE_ORDER.map((item) => {
    const rows = matchesByStage?.[item.responseKey] ?? [];

    return {
      key: item.responseKey,
      title: tMatchData(t, item.i18nKey),
      count: rows.length,
      rows,
    };
  });
}

const KpiCard = memo((props: KpiCardItem) => (
  <Card sx={{ p: 3, borderLeft: 4, borderColor: props.borderColor }}>
    <Typography variant="body2" color="text.secondary">
      {props.title}
    </Typography>

    <Typography variant="h4" fontWeight={700} sx={{ mt: 1 }}>
      {props.value}
    </Typography>
  </Card>
));

KpiCard.displayName = 'KpiCard';

const SyncStatusCard = memo(({ syncStatus }: { syncStatus: MatchSyncStatus | null }) => {
  const t = useTranslate();

  return (
    <Card sx={{ p: 3 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6">{tMatchData(t, 'syncStatus.title')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {tMatchData(t, 'syncStatus.desc')}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
          gap: 2,
        }}
      >
        <Stack spacing={0.5}>
          <Typography variant="body2" fontWeight={600}>
            {tMatchData(t, 'syncStatus.endpoint')}
          </Typography>
          <TextField
            size="small"
            fullWidth
            value={syncStatus?.endpoint ?? '--'}
            InputProps={{ readOnly: true, sx: { fontFamily: 'monospace' } }}
          />
        </Stack>

        <Stack spacing={0.5}>
          <Typography variant="body2" fontWeight={600}>
            {tMatchData(t, 'syncStatus.lastSync')}
          </Typography>
          <TextField
            size="small"
            fullWidth
            value={syncStatus?.lastSyncAt ? formatRelativeTime(syncStatus.lastSyncAt) : '--'}
            InputProps={{ readOnly: true }}
          />
        </Stack>
      </Box>
    </Card>
  );
});

SyncStatusCard.displayName = 'SyncStatusCard';

const MatchStageCard = memo(
  ({
    title,
    count,
    rows,
    onPatchMatch,
  }: {
    title: string;
    count: number;
    rows: WorldCupMatch[];
    onPatchMatch: (matchId: string, payload: PatchWorldCupMatchPayload) => Promise<void>;
  }) => {
    const t = useTranslate();

    return (
      <Card sx={{ p: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6">{title}</Typography>
          <Typography variant="body2" color="text.secondary">
            {tMatchData(t, 'matches.sectionCount', { count })}
          </Typography>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{tMatchData(t, 'table.stage')}</TableCell>
                <TableCell>{tMatchData(t, 'table.matchup')}</TableCell>
                <TableCell>{tMatchData(t, 'table.kickoffAt')}</TableCell>
                <TableCell>{tMatchData(t, 'table.score')}</TableCell>
                <TableCell>{tMatchData(t, 'table.status')}</TableCell>
                <TableCell sx={{ minWidth: 200 }}>
                  <Stack direction="row" alignItems="center" spacing={0.5} component="span">
                    {tMatchData(t, 'table.matchSlug')}
                    <Tooltip title={tMatchData(t, 'table.matchSlugHint')} arrow placement="top">
                      <Iconify
                        icon="eva:info-outline"
                        width={18}
                        sx={{ color: 'text.secondary', cursor: 'help' }}
                      />
                    </Tooltip>
                  </Stack>
                </TableCell>
                <TableCell sx={{ minWidth: 240 }}>{tMatchData(t, 'table.watchLiveLink')}</TableCell>
                <TableCell sx={{ width: 100 }}>{tMatchData(t, 'table.action')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length ? (
                rows.map((match) => (
                  <MatchRow key={match.id} match={match} onPatchMatch={onPatchMatch} />
                ))
              ) : (
                <EmptyRow text={tMatchData(t, 'matches.emptyStage')} />
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    );
  }
);

MatchStageCard.displayName = 'MatchStageCard';

const MatchRow = memo(
  ({
    match,
    onPatchMatch,
  }: {
    match: WorldCupMatch;
    onPatchMatch: (matchId: string, payload: PatchWorldCupMatchPayload) => Promise<void>;
  }) => {
    const t = useTranslate();
    const locale = useLocale();
    const timeText = getMatchTimeText(match, t);
    const score = getMatchScore(match);
    const isLive = match.status === 'live';

    const originalSlug = match.predictionEventSlug ?? '';
    const originalLink = match.watchLiveLink ?? '';

    const [slugDraft, setSlugDraft] = useState(originalSlug);
    const [linkDraft, setLinkDraft] = useState(originalLink);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
      setSlugDraft(originalSlug);
      setLinkDraft(originalLink);
    }, [originalSlug, originalLink]);

    const isDirty = slugDraft !== originalSlug || linkDraft !== originalLink;

    const handleSave = useCallback(async () => {
      setSaving(true);
      try {
        await onPatchMatch(match.id, {
          predictionEventSlug: slugDraft.trim(),
          watchLiveLink: linkDraft.trim(),
        });
      } catch {
        // notify handled upstream
      } finally {
        setSaving(false);
      }
    }, [linkDraft, match.id, onPatchMatch, slugDraft]);

    const mapStage = (item: WorldCupMatch) => {
      const stageLabel = item.stage || item.stageLabel || '';
      if (stageLabel === 'group') {
        return t('module.worldCup.focusMatch.stage.group', { group: item.groupName || '--' });
      }
      return t(`module.worldCup.focusMatch.stage.${stageLabel}`) || stageLabel || '--';
    };

    return (
      <TableRow hover sx={{ position: 'relative' }}>
        <TableCell>
          <Chip label={mapStage(match)} size="small" variant="outlined" />
        </TableCell>

        <TableCell>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <TeamSlot team={match.homeTeam} placeholder={match.homePlaceholder} locale={locale} />
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
              {tMatchData(t, 'table.vs')}
            </Typography>
            <TeamSlot team={match.awayTeam} placeholder={match.awayPlaceholder} locale={locale} />
          </Stack>
        </TableCell>

        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {timeText === 'finished' ? tMatchData(t, 'match.status.finished') : timeText}
          </Typography>
        </TableCell>

        <TableCell>
          <Typography variant="body2" fontWeight={700}>
            {score}
          </Typography>
        </TableCell>

        <TableCell>
          {isLive ? (
            <Chip
              icon={<Iconify icon="eva:radio-fill" />}
              label={`LIVE ${score}`}
              color="success"
              size="small"
              sx={{ mr: 1 }}
            />
          ) : (
            <Chip
              label={tMatchData(t, `match.status.${getMatchStatusKey(match.status)}`)}
              color={getMatchStatusColor(match.status)}
              size="small"
              sx={{ mr: 1 }}
            />
          )}
          {match.lastSyncAt ? (
            <Typography variant="caption" color="text.disabled">
              {tMatchData(t, 'table.lastSync', {
                time: formatShortTime(match.lastSyncAt),
              })}
            </Typography>
          ) : null}
        </TableCell>

        <TableCell>
          <TextField
            size="small"
            fullWidth
            value={slugDraft}
            placeholder={tMatchData(t, 'table.matchSlugPlaceholder')}
            onChange={(event) => setSlugDraft(event.target.value)}
            disabled={saving}
          />
        </TableCell>

        <TableCell>
          <TextField
            size="small"
            fullWidth
            value={linkDraft}
            placeholder={tMatchData(t, 'table.watchLiveLinkPlaceholder')}
            onChange={(event) => setLinkDraft(event.target.value)}
            disabled={saving}
          />
        </TableCell>

        <TableCell>
          <PermissionButton resource="world-cup-match-data" action="write">
            <Button
              size="small"
              variant="contained"
              disabled={!isDirty || saving}
              onClick={handleSave}
            >
              {saving ? t('common.saving') : tMatchData(t, 'actions.save')}
            </Button>
          </PermissionButton>
        </TableCell>
      </TableRow>
    );
  }
);

MatchRow.displayName = 'MatchRow';

const TeamSlot = memo(
  ({
    team,
    placeholder,
    locale,
  }: {
    team: WorldCupMatch['homeTeam'];
    placeholder?: string | null;
    locale?: string;
  }) => {
    if (!team) {
      return (
        <Typography variant="body2" color="text.secondary">
          {placeholder || '--'}
        </Typography>
      );
    }

    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
        {team.flagEmoji ? (
          <Typography component="span" variant="body2">
            {team.flagEmoji}
          </Typography>
        ) : null}

        <Typography variant="body2" fontWeight={700} noWrap>
          {getTeamName(team, locale)}
        </Typography>

        {team.code ? (
          <Typography variant="caption" color="text.secondary">
            {team.code}
          </Typography>
        ) : null}
      </Stack>
    );
  }
);

TeamSlot.displayName = 'TeamSlot';

const EmptyRow = memo(({ text }: { text: string }) => (
  <TableRow>
    <TableCell colSpan={8}>
      <Stack alignItems="center" sx={{ py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          {text}
        </Typography>
      </Stack>
    </TableCell>
  </TableRow>
));

EmptyRow.displayName = 'EmptyRow';

const CenterBox = ({ children }: { children: ReactNode }) => (
  <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 360 }}>
    {children}
  </Stack>
);
